import { useState } from "react";
import { AlertTriangle, CheckCircle2, ArrowRight, Eye } from "lucide-react";
import { cn } from "@/lib/utils";

const beforeRequirement = {
  title: "User Story: Checkout Flow",
  content: `As a user, I want to be able to checkout quickly so that I can complete my purchase.

Acceptance Criteria:
- Users should be able to checkout
- Payment should work
- It should be fast`,
  issues: [
    "No definition of 'quickly' or 'fast'",
    "No error handling specified",
    "Missing payment method details",
    "No edge cases covered",
  ],
  score: 34,
};

const afterRequirement = {
  title: "User Story: Checkout Flow (Improved)",
  content: `As a returning customer with items in cart, I want to complete checkout in under 60 seconds using saved payment methods, so I can minimize friction and complete purchases on mobile.

Acceptance Criteria:
- Checkout completes in ≤3 steps for returning users
- Supports Stripe, PayPal, Apple Pay
- Shows real-time validation errors inline
- Handles failed payments with retry option
- Works offline-first with sync on reconnect

Assumptions:
- User is authenticated
- Cart validation happens before checkout
- Inventory is reserved for 15 minutes`,
  improvements: [
    "Explicit timing constraint (60s, 3 steps)",
    "Specific payment providers listed",
    "Error handling defined",
    "Edge cases addressed",
  ],
  score: 92,
};

export function BeforeAfterDemo() {
  const [activeTab, setActiveTab] = useState<"before" | "after">("before");

  return (
    <section className="py-20 md:py-32 relative overflow-hidden">
      {/* Background ambient */}
      <div className="absolute top-[20%] right-[10%] w-[400px] h-[400px] rounded-full bg-primary/[0.05] blur-[140px]" />
      <div className="absolute bottom-[10%] left-[5%] w-[300px] h-[300px] rounded-full bg-accent/[0.04] blur-[120px]" />

      <div className="container relative z-10 px-4 md:px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Eye className="w-4 h-4" />
            See the difference
          </div>
          <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
            See the difference Reqqy makes
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Watch a vague user story transform into clear, actionable requirements
          </p>
        </div>

        {/* Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex p-1 rounded-full glass border border-border/50">
            <button
              onClick={() => setActiveTab("before")}
              className={cn(
                "px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300",
                activeTab === "before"
                  ? "bg-destructive/20 text-destructive shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Before Reqqy
            </button>
            <button
              onClick={() => setActiveTab("after")}
              className={cn(
                "px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300",
                activeTab === "after"
                  ? "gradient-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              After Reqqy
            </button>
          </div>
        </div>

        {/* Card */}
        <div className="max-w-4xl mx-auto">
          <div
            className={cn(
              "rounded-2xl glass p-6 md:p-8 transition-all duration-500 border",
              activeTab === "before"
                ? "border-destructive/20 shadow-[0_0_60px_-20px_hsl(0_84%_60%/0.1)]"
                : "border-primary/20 shadow-[0_0_60px_-20px_hsl(var(--primary)/0.15)]"
            )}
          >
            {/* Score Badge */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">
                {activeTab === "before" ? beforeRequirement.title : afterRequirement.title}
              </h3>
              <div
                className={cn(
                  "px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-500",
                  activeTab === "before"
                    ? "bg-destructive/20 text-destructive"
                    : "bg-primary/20 text-primary"
                )}
              >
                Clarity Score:{" "}
                {activeTab === "before" ? beforeRequirement.score : afterRequirement.score}%
              </div>
            </div>

            {/* Content */}
            <div className={cn(
              "rounded-xl p-5 mb-6 font-mono text-sm whitespace-pre-wrap border transition-all duration-500",
              activeTab === "before"
                ? "bg-destructive/[0.03] border-destructive/10"
                : "bg-primary/[0.03] border-primary/10"
            )}>
              {activeTab === "before" ? beforeRequirement.content : afterRequirement.content}
            </div>

            {/* Issues / Improvements */}
            <div>
              <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                {activeTab === "before" ? (
                  <>
                    <AlertTriangle className="w-4 h-4 text-destructive" />
                    Issues Found
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-primary" />
                    Improvements Made
                  </>
                )}
              </h4>
              <div className="grid sm:grid-cols-2 gap-2">
                {(activeTab === "before" ? beforeRequirement.issues : afterRequirement.improvements).map(
                  (item, i) => (
                    <div
                      key={i}
                      className={cn(
                        "group flex items-start gap-2 text-sm p-3 rounded-xl transition-all duration-300",
                        activeTab === "before"
                          ? "bg-destructive/[0.06] hover:bg-destructive/[0.1]"
                          : "bg-primary/[0.06] hover:bg-primary/[0.1]"
                      )}
                    >
                      {activeTab === "before" ? (
                        <AlertTriangle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110" />
                      )}
                      {item}
                    </div>
                  )
                )}
              </div>
            </div>
          </div>

          {/* Arrow indicator */}
          {activeTab === "before" && (
            <div className="flex justify-center mt-8">
              <button
                onClick={() => setActiveTab("after")}
                className="group flex items-center gap-2 text-primary hover:text-primary/80 transition-all duration-300"
              >
                <span className="text-sm font-medium">See improvement</span>
                <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
